import { AgenticHubAction, type AgenticHubPayload } from '@numeo/types'
import { useGeneralAgentChatStore } from './generalAgentChatStore'

/**
 * The general agent's share of the `AH_*` socket lane: its events carry `chatVariant: 'general'` (ADR-048). A turn's
 * end or failure ends the chat's wait; every other event of the turn (its start, the heartbeat) keeps the stall
 * watchdog quiet, and a turn whose events stop reaching us is settled by one read of the stored messages. A card's
 * question and its end are the general agent's only, and some are sent without the variant (an answer's or a new
 * message's), so they are taken by their tab; replayed after a reconnect, they land on the same question.
 */
export function handleGeneralAgentSocketEvent(payload: AgenticHubPayload): void {
    if (!('tabId' in payload)) return
    const store = useGeneralAgentChatStore.getState()
    if (payload.action === AgenticHubAction.AH_ToolQuestion) return store.questionAsked(payload)
    if (payload.action === AgenticHubAction.AH_ToolQuestionResolved) {
        if (payload.tabId === store.tabId) store.questionResolved(payload.questionId, payload.outcome)
        return
    }
    if (!('chatVariant' in payload) || payload.chatVariant !== 'general') return
    if (payload.action === AgenticHubAction.AH_Error) {
        // A run replaced by the user's next message (or stopped by them) is not a failure: the new turn carries on.
        if (payload.code === 'SUPERSEDED' || payload.code === 'ABORTED') store.turnActive(payload.tabId)
        else store.turnFailed(payload.tabId, payload.message)
    } else if (payload.action === AgenticHubAction.AH_StreamEnd) store.turnEnded(payload.tabId)
    else store.turnActive(payload.tabId)
}

/** The socket closed or failed: a turn still waiting gets no more events on it, so its wait ends now with a Retry. */
export function handleGeneralAgentStreamLost(): void {
    useGeneralAgentChatStore.getState().streamLost()
}

/** After a reconnect: the socket-service replay covers the AI Hub's tabs only, so the open chat reads its waiting question again. */
export function refreshGeneralAgentAfterReconnect(): void {
    useGeneralAgentChatStore
        .getState()
        .resync()
        .catch(() => undefined)
}
